import React from 'react';
import { Loading, Container, Text, Button, EmptyState } from '../../components';
import amigoIconSmall from '../../assets/images/amigo-icon-small.svg';
import { COLORS } from '../../constants/theme';
import { CLINIC_REASONS, DOCTOR_REASONS, LOW_RATING_THRESHOLD} from '../../constants/attendanceEvaluation';
import { Card } from '../../components/styles/FormCard.styles';
import {
	CompanyHeader,
	CompanyDivider,
	CardSection,
	SectionDivider,
	SectionHeader,
	IconBadge,
	CompanyPhoto,
	DoctorAvatar,
	DoctorAvatarImage,
	StarRow,
	StarButton,
	ReasonGroup,
	ChipRow,
	Chip,
	CommentTextarea,
	ObservationCounter,
	ObservationFieldWrapper,
	SubmittedState,
	SubmittedIcon,
	SubmittedText
} from './AttendanceEvaluationForm.styles';

const isLowRating = rating => rating > 0 && rating <= LOW_RATING_THRESHOLD;

const StarIcon = ({ filled }) => (
	<i
		className={`doca-icon doca-icon__star${filled ? ' doca-icon--solid' : ''}`}
		style={{ fontSize: 30, color: filled ? COLORS.primary.pure : COLORS.light.high }}
	/>
);

const StarRating = ({ value, onChange }) => (
	<StarRow>
		{[1, 2, 3, 4, 5].map(star => (
			<StarButton key={star} type="button" onClick={() => onChange(star)}>
				<StarIcon filled={star <= value} />
			</StarButton>
		))}
	</StarRow>
);

const ReasonChips = ({ label, options, selected, onToggle }) => (
	<ReasonGroup>
		<Text size="small" weight="medium">{label}</Text>
		<ChipRow>
			{options.map(option => (
				<Chip
					key={option.value}
					type="button"
					selected={selected.includes(option.value)}
					onClick={() => onToggle(option.value)}
				>
					{option.label}
				</Chip>
			))}
		</ChipRow>
	</ReasonGroup>
);

const RatingSection = ({ rating, reasonsLabel, reasonsOptions, observationName, maxObservationLength, onChange }) => (
	<>
		<StarRating value={rating.rating || 0} onChange={rating.onSetRating} />

		<Text size="small" variant="gray">{rating.hint}</Text>

		{isLowRating(rating.rating) && (
			<ReasonChips
				label={reasonsLabel}
				options={reasonsOptions}
				selected={rating.reasons || []}
				onToggle={rating.onToggleReason}
			/>
		)}

		<ObservationField
			name={observationName}
			value={rating.observation || ''}
			maxLength={maxObservationLength}
			onChange={onChange}
		/>
	</>
);

const ObservationField = ({ name, value, maxLength, onChange }) => (
	<ObservationFieldWrapper>
		<CommentTextarea
			name={name}
			placeholder="Quer contar mais alguma coisa? (opcional)"
			value={value}
			maxLength={maxLength}
			onChange={onChange}
		/>
		<ObservationCounter exceeded={value.length > maxLength}>
			{value.length}/{maxLength}
			{value.length > maxLength && ' — limite de caracteres excedido'}
		</ObservationCounter>
	</ObservationFieldWrapper>
);

function AttendanceEvaluationForm({
	loading,
	linkExpired,
	submitted,
	variant,
	companyName,
	companyLogoUrl,
	unitName,
	patientName,
	attendanceIntro,
	clinic,
	doctor,
	canSubmit,
	maxObservationLength,
	onChange,
	onSubmitClick
}) {
	if (loading) {
		return <Loading />;
	}

	if (linkExpired) {
		return (
			<EmptyState
				icon="link-slash"
				title="Link inválido ou expirado"
				description="Solicite um novo link para avaliar seu atendimento."
			/>
		);
	}

	if (submitted) {
		return (
			<Container padded>
				<SubmittedState>
					<SubmittedIcon>
						<i className="doca-icon doca-icon__circle-check" />
					</SubmittedIcon>
					<Text size="large" weight="semibold" textAlign="center">Avaliação enviada</Text>
					<SubmittedText>
						<Text variant="gray" textAlign="center">
							{patientName ? `Obrigado, ${patientName}. ` : 'Obrigado. '}
							Sua opinião vai direto para a equipe da clínica.
						</Text>
					</SubmittedText>
					<img src={amigoIconSmall} alt="" width={32} height={32} />
				</SubmittedState>
			</Container>
		);
	}

	return (
		<Container padded>
			<Card>
				<CardSection>
					{companyName && (
						<CompanyHeader>
							<img src={amigoIconSmall} alt="" width={32} height={32} />
							<CompanyDivider />
							<div>
								<Text weight="semibold">{companyName}</Text>
								{unitName && <Text size="small" variant="gray">{unitName}</Text>}
							</div>
						</CompanyHeader>
					)}

					<Text size="large" weight="semibold">
						{patientName ? `Olá, ${patientName}` : 'Olá!'}
					</Text>
					<Text variant="gray">{attendanceIntro}</Text>
				</CardSection>

				<SectionDivider />

				<form onSubmit={onSubmitClick}>
					<CardSection>
						<SectionHeader>
							{companyLogoUrl ? (
								<CompanyPhoto src={companyLogoUrl} alt="" />
							) : (
								<IconBadge>
									<i className="doca-icon doca-icon--large doca-icon__hospital" />
								</IconBadge>
							)}
							<div>
								<Text weight="semibold">Atendimento da clínica</Text>
								<Text size="small" variant="gray">Recepção, espera e estrutura</Text>
							</div>
						</SectionHeader>

						<RatingSection
							rating={clinic}
							reasonsLabel="O que não funcionou bem?"
							reasonsOptions={CLINIC_REASONS}
							observationName="clinic_observation"
							maxObservationLength={maxObservationLength}
							onChange={onChange}
						/>
					</CardSection>

					<SectionDivider />

					<CardSection>
						<SectionHeader>
							<DoctorAvatar>
								{doctor.photoUrl ? <DoctorAvatarImage src={doctor.photoUrl} alt="" /> : doctor.initials}
							</DoctorAvatar>
							<div>
								<Text weight="semibold">{[doctor.title, doctor.name].filter(Boolean).join(' ')}</Text>
								{doctor.line && <Text size="small" variant="gray">{doctor.line}</Text>}
							</div>
						</SectionHeader>

						<RatingSection
							rating={doctor}
							reasonsLabel="O que faltou no atendimento?"
							reasonsOptions={DOCTOR_REASONS}
							observationName="doctor_observation"
							maxObservationLength={maxObservationLength}
							onChange={onChange}
						/>
					</CardSection>

					<SectionDivider />

					<CardSection>
						<Button block size="large" variant={variant} disabled={!canSubmit} type="submit">
							Enviar avaliação
						</Button>
						<Text size="small" variant="gray" textAlign="center">
							Sua resposta é usada apenas para melhorar o atendimento.
						</Text>
					</CardSection>
				</form>
			</Card>
		</Container>
	);
}

export default AttendanceEvaluationForm;
